import React from "react";
import "./App.css";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import Login from "./pages/Login/Login";
import Logout from "./pages/Logout/Logout";
import Register from "./pages/Register/Register";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { SiteProvider } from "./context/siteContext";
import { ThemeContextProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/cartContext";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import AuthProvider from "./context/authContext";
import { useAuth } from "./context/authContext";
import OptionsProvider from "./context/optionContext";
import { useOptions } from "./context/optionContext";
import NotificationProvider from "./context/NotificationContext";
import { ToastProvider } from "./context/ToastContext";
import ProtectedRoute from "./ProtectedRoute";
import PageNotFound from "./pages/PageNotFound/PageNotFound";
import ErrorBoundary from "./CentralError";
import Analytics from "./pages/Analytics/Analytics";
import AcceptEULA from "./pages/AcceptEULA/AcceptEULA";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import SpecialProtectedRoute from "./SpecialProtectedRoute";
import { ROUTES } from './constants';
import DefaultLayout from "./layouts/DefaultLayout/DefaultLayout";
import Layout2 from "./layouts/Layout2/Layout2";
import { DEFAULT_LAYOUT } from "./config";
import ComplianceProvider from "./context/complianceContext";
import 'shepherd.js/dist/css/shepherd.css';
import { TourProvider, useTour } from "./context/TourContext";
import TourButton from "./components/TourButton/TourButton";
import ChatPopup from "./components/ChatPopup/ChatPopup"; // CHIP
import CookieConsentBanner from "./components/CompliancePolicy/CookieConsentBanner";
import './pages/Tours/ShepherdCustom.css'
import ModelViewer from "./pages/ModelViewer/ModelViewer";
import PDFModelViewer from "./pages/PDFModelViewer/PDFModelViewer";
import PublicModelCheck from "./pages/PublicModelCheck/PublicModelCheck";
import OpenPanelPageTracker from "./components/Analytics/OpenPanelPageTracker";
import { canAccessChatbot } from "./utilities/pageAccess";


const AppProviders = ({ children }) => (
    <ErrorBoundary>
        <OptionsProvider>
            <AuthProvider>
                <ComplianceProvider>
                    <NotificationProvider>
                        <ToastProvider>
                            <ThemeContextProvider>
                                <SiteProvider>
                                    <CartProvider>
                                        <TourProvider>
                                            {children}
                                        </TourProvider>
                                    </CartProvider>
                                </SiteProvider>
                            </ThemeContextProvider>
                        </ToastProvider>
                    </NotificationProvider>
                </ComplianceProvider>
            </AuthProvider>
        </OptionsProvider>
    </ErrorBoundary>
);

const AppRoutes = () => (
    <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path={ROUTES.REGISTER} element={<Register />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={ROUTES.RESET_PASSWORD_WITH_TOKEN} element={<ResetPassword />} />
        <Route path={ROUTES.PUBLIC_MODEL_CHECK} element={<PublicModelCheck />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
            <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
            <Route path={ROUTES.ACCEPT_EULA} element={<AcceptEULA />} />
            <Route element={<SpecialProtectedRoute />}>
                <Route path={`${ROUTES.MODEL_VIEWER}/:machine_id/:fileName`} element={<ModelViewer />} />
                <Route path={`${ROUTES.TWOD_MODEL_VIEWER}/:machine_id/:fileName`} element={<PDFModelViewer />} />
                {DEFAULT_LAYOUT.toLowerCase() === "layout2" ? (
                    <>
                        <Route path={"/*"} element={<Layout2 />} />
                    </>
                ) : (
                    <>
                        <Route path={"/*"} element={<DefaultLayout />} />
                    </>
                )}
            </Route>
        </Route>

        {/* Logout Route */}
        <Route path={ROUTES.LOGOUT} element={<Logout />} />
    </Routes>
);

// Conditional TourButton component
const ConditionalTourButton = () => {
    const location = useLocation();
    const { hasTourSteps } = useTour();
    
    const showTourButton = hasTourSteps();
    
    const isPublicRoute = [
        ROUTES.LOGIN,
        ROUTES.REGISTER,
        ROUTES.FORGOT_PASSWORD,
        ROUTES.RESET_PASSWORD,
        ROUTES.VERIFY_EMAIL,
        ROUTES.ACCEPT_EULA,
        ROUTES.LOGOUT
    ].some(route => location.pathname.startsWith(route));


    if (isPublicRoute || !showTourButton) {
        return null;
    }
    
    return <TourButton />;
};

const ConditionalChatPopup = () => {
    const location = useLocation();
    const { optionKeys } = useOptions();
    const { user, hasPermission } = useAuth();

    const isPublicRoute = [
        ROUTES.LOGIN,
        ROUTES.REGISTER,
        ROUTES.FORGOT_PASSWORD,
        ROUTES.RESET_PASSWORD,
        ROUTES.VERIFY_EMAIL,
        ROUTES.ACCEPT_EULA,
        ROUTES.LOGOUT,
        ROUTES.PUBLIC_MODEL_CHECK,
    ].some(route => location.pathname.startsWith(route));

    const showChatbot =
        !isPublicRoute &&
        optionKeys?.enableChatbot !== "false" &&
        canAccessChatbot(hasPermission, user);

    if (!showChatbot) {
        return null;
    }

    return <ChatPopup />;
};

const App = () => {
    return (
        <AppProviders>
            <OpenPanelPageTracker />
            <Box display="flex" flexDirection="column" minHeight="100vh" className="main-body">
                <Container maxWidth={false} component="main" className="main-container">
                    <AppRoutes />
                </Container>
                
                {/* ADD ALL THREE COMPONENTS HERE */}

                <ConditionalTourButton />
                <CookieConsentBanner />
                <ConditionalChatPopup />
            </Box>
        </AppProviders>
    );
};

const AppWithRouter = () => (
    <Router>
        <App />
    </Router>
);

export default AppWithRouter;
